import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, catchError, of, retry, tap, timer } from 'rxjs';
import { ApiConfigurationDTO } from './api-configuration.interface';
import { environment } from 'src/environments/environment';

const STORAGE_KEY = 'apiConfigurationOverrides';

// Resolves and caches this tenant's per-module gateway base URLs
// (ServerPool.ERP/CRM/AM/... via CompanyMaster.ServerId). ApiUrlRewriteInterceptor
// reads getOverrides() on every outgoing request and swaps the origin of any
// request that matches one of the compiled-in environment.*BaseURL defaults.
//
// If this call never succeeds (ServerId not assigned yet, or the request
// itself fails after retrying), getOverrides() returns null and
// ApiUrlRewriteInterceptor blocks tenant-routed requests outright instead of
// guessing at the compiled-in default host - see that file's null-overrides
// branch.
@Injectable({
  providedIn: 'root'
})
export class ApiConfigurationService {
  http = inject(HttpClient);

  private overrides: ApiConfigurationDTO | null = this.loadFromStorage();

  private loadFromStorage(): ApiConfigurationDTO | null {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (!cached) return null;
    try {
      return JSON.parse(cached);
    } catch {
      return null;
    }
  }

  // Call once per session, right after login/token validation resolves the
  // authenticated user's companyId (== TenantId on the backend).
  //
  // Hardcoded to AdvinPROD01 rather than built from environment.AMBaseURL:
  // this call must always reach the server hosting the [UseIdentityServer]
  // endpoints (IDENTITYDBConn), regardless of which tenant/pool this browser
  // ultimately resolves to. Same reasoning as the hardcoded URL in
  // ValueListService.ValueListItem_GetFilteredKeyValueExtendedByCode.
  loadConfiguration(tenantId: number | string): Observable<ApiConfigurationDTO | null> {
    return this.http.get<ApiConfigurationDTO>(`${environment.APIConfig}${tenantId}`).pipe(
      // A single transient blip (timeout, brief network drop) here used to
      // permanently drop the tenant override for the whole session - every
      // request after it silently fell back to the compiled-in default
      // gateway host. Ride out short-lived failures before giving up.
      retry({ count: 2, delay: () => timer(500) }),
      tap(config => {
        this.overrides = config;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
      }),
      catchError(() => {
        this.overrides = null;
        localStorage.removeItem(STORAGE_KEY);
        return of(null);
      })
    );
  }

  // Called from SignOutUser() — localStorage.clear() already drops the cached
  // key, but the service instance itself is a singleton that survives an
  // in-SPA logout/login cycle (no page reload), so the in-memory field needs
  // clearing explicitly too.
  clearConfiguration(): void {
    this.overrides = null;
    localStorage.removeItem(STORAGE_KEY);
  }

  getOverrides(): ApiConfigurationDTO | null {
    return this.overrides;
  }

  // Page-scoped variant of loadConfiguration() for admin screens that view
  // ANOTHER tenant's data (e.g. Customer Details) and need THAT tenant's
  // gateway URLs for their read calls, without disturbing this service's
  // own session-wide cache (which must keep reflecting the LOGGED-IN
  // user's own tenant for ApiUrlRewriteInterceptor's app-wide rewriting).
  // Deliberately does not touch `this.overrides` or localStorage - callers
  // hold onto the returned DTO themselves (e.g. as a component field) and
  // pass individual fields through as a per-request baseUrlOverride via
  // resolveTenantUrl(). Same retry/fallback behavior as loadConfiguration()
  // otherwise: rides out a transient blip, resolves to null if the tenant
  // has no active ServerPool assignment yet or the call keeps failing.
  fetchConfigurationForTenant(tenantId: number | string): Observable<ApiConfigurationDTO | null> {
    return this.http.get<ApiConfigurationDTO>(`${environment.APIConfig}${tenantId}`).pipe(
      retry({ count: 2, delay: () => timer(500) }),
      catchError(() => of(null))
    );
  }
}
